"use client";

import * as React from "react";
import { Search, ChevronsUpDown, Loader2, Package } from "lucide-react";
import { Button } from "../utils/components/ui/button";
import { cn } from "../utils/cn";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../utils/components/ui/popover";
import { formatProductDropdownLabel } from "../utils/productDropdown";
import { formatCostCode } from "../utils/costCode";

import { ProductT } from "../types/productTypes";

export interface ProductComboOption {
  value: string;
  label: string;
  rawProduct?: ProductT;
  [key: string]: any;
}

export interface ProductDropdownProps {
  options?: ProductComboOption[];
  products?: ProductT[];
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  defaultValue?: string;
  placeholder?: string;
  className?: string;
  allowAddOption?: boolean;
  useApiFilter?: boolean;
  apiFilterFunction?: (input: string) => void;
  loading?: boolean;
  disabled?: boolean;
  currencySymbol?: string;
}

const getBadgeStyle = (label: string) => {
  if (!label) return "bg-[#F1F5F9] text-[#64748B]";
  const colors = [
    "bg-[#EEF2FF] text-[#6366F1]", // Indigo
    "bg-[#E0F2FE] text-[#0284C7]", // Sky
    "bg-[#FEF3C7] text-[#D97706]", // Amber
    "bg-[#DCFCE7] text-[#16A34A]", // Green
    "bg-[#FCE7F3] text-[#DB2777]", // Pink
    "bg-[#F3E8FF] text-[#9333EA]", // Purple
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = label.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

export const ProductDropdown: React.FC<ProductDropdownProps> = ({
  options,
  products,
  value,
  onChange,
  placeholder = "Select a product...",
  className = "",
  useApiFilter = false,
  apiFilterFunction,
  loading = false,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Derive options if raw products array is supplied
  const derivedOptions: ProductComboOption[] = React.useMemo(() => {
    if (products && Array.isArray(products) && products.length > 0) {
      return products.map((product) => ({
        value: String(product?.id),
        label: formatProductDropdownLabel(product),
        rawProduct: product,
      }));
    }
    if (options && Array.isArray(options)) {
      return options;
    }
    return [];
  }, [options, products]);

  // Handle Search Input Change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (useApiFilter && apiFilterFunction) {
      apiFilterFunction(val);
    }
  };

  // Filter Options Client-Side as well for snappy UX
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return derivedOptions;
    }
    const query = searchQuery.toLowerCase().trim();
    return derivedOptions.filter((opt) => {
      const raw = opt.rawProduct;
      const searchTarget = [
        opt.label,
        raw?.productName,
        raw?.keyword,
        raw?.rackLabel,
        raw?.unit,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchTarget.includes(query);
    });
  }, [derivedOptions, searchQuery]);

  // Extract display details for an item
  const getItemDetails = (item: ProductComboOption) => {
    const raw = item.rawProduct || {};
    const productName =
      raw.productName ||
      item.label?.split("(")[0]?.trim() ||
      item.label ||
      "Product";
    const imageUrl = raw.imageUrl || null;
    const unit = raw.unit || "NOS";
    const rackLabel = raw.rack.rackLabel;
    const price =
      typeof raw.price === "number"
        ? raw.price
        : typeof raw.buyingPrice === "number"
          ? raw.buyingPrice
          : 0;

    const stockQty =
      raw.availableQuantity ?? raw.qty ?? raw.displayQuantity ?? "";
    const stockText =
      typeof stockQty === "number"
        ? `${stockQty} ${unit}`
        : stockQty
          ? String(stockQty)
          : "0 NOS";

    const costCode = formatCostCode(raw.latestPurchasePrice);

    return {
      productName,
      imageUrl,
      unit,
      rackLabel,
      price,
      stockText,
      costCode,
    };
  };

  // Find label of currently selected value
  const selectedLabel = React.useMemo(() => {
    if (value === null || value === undefined || value === "") return "";
    const match = derivedOptions.find(
      (opt) => String(opt.value) === String(value),
    );
    if (!match) return "";
    const details = getItemDetails(match);
    return details.productName || match.label;
  }, [derivedOptions, value]);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setSearchQuery("");
  };

  // Focus search input when popover opens
  React.useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full h-11 justify-between rounded-[12px] border border-[#E5ECF8] bg-[#F4F7FC] px-4 py-2 text-sm font-normal text-[#23324D] shadow-none hover:bg-[#EEF4FF] focus:border-[#4C7DF2] focus:ring-1 focus:ring-[#4C7DF2]",
            !selectedLabel && "text-[#8A948D]",
            className,
          )}
        >
          <span className="truncate text-left flex-1 font-medium text-[#23324D]">
            {selectedLabel || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-[#8A948D] opacity-80" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[var(--radix-popover-trigger-width)] min-w-[640px] max-w-[760px] p-4 rounded-[20px] border border-[#E5ECF8] bg-white shadow-[0_16px_40px_rgba(0,0,0,0.12)] z-50"
      >
        {/* Search Input Box */}
        <div className="relative mb-3">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search products..."
            className="w-full h-11 rounded-[12px] border border-[#E2E8F0] bg-white pl-10 pr-4 text-sm text-[#1E293B] placeholder-[#94A3B8] outline-none transition focus:border-[#4C7DF2] focus:ring-1 focus:ring-[#4C7DF2]"
          />
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#F1F5F9] text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
          <div className="col-span-5 text-left">PRODUCT</div>
          <div className="col-span-2 text-center">RACK LABEL</div>
          <div className="col-span-1 text-center">UNIT</div>
          <div className="col-span-2 text-right">VALUE / PRICE</div>
          <div className="col-span-2 text-right">STOCK</div>
        </div>

        {/* Table Rows List */}
        <div
          className="max-h-[280px] overflow-y-auto divide-y divide-[#F1F5F9]"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#CBD5E1 transparent",
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-10 text-xs font-semibold text-[#64748B] gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-[#4C7DF2]" />
              Loading products...
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="py-10 text-center text-xs font-semibold text-[#64748B]">
              No products found
            </div>
          ) : (
            filteredOptions.map((item) => {
              const isSelected = String(value) === String(item.value);
              const details = getItemDetails(item);

              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handleSelect(item.value)}
                  className={cn(
                    "w-full text-left grid grid-cols-12 gap-2 items-center px-3 py-3 transition-colors cursor-pointer",
                    isSelected ? "bg-[#F0F5FF]" : "hover:bg-[#F4F7FF]",
                  )}
                >
                  {/* Column 1: Product (Image + Name + Subtext) */}
                  <div className="col-span-5 flex items-center min-w-0 pr-2">
                    <div className="w-10 h-10 shrink-0 rounded-[8px] border border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-center overflow-hidden mr-3">
                      {details.imageUrl ? (
                        <img
                          src={details.imageUrl}
                          alt={details.productName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <Package className="w-5 h-5 text-[#94A3B8]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-[#0F172A] truncate leading-tight">
                        {details.productName}
                      </p>
                      <p className="text-xs text-[#64748B] mt-0.5 font-normal">
                        Unit: {details.unit}
                      </p>
                    </div>
                  </div>

                  {/* Column 2: Rack Label */}
                  <div className="col-span-2 text-center">
                    {details.rackLabel ? (
                      <span
                        className={cn(
                          "inline-block rounded-[6px] px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase",
                          getBadgeStyle(details.rackLabel),
                        )}
                      >
                        {details.rackLabel}
                      </span>
                    ) : (
                      <span className="text-xs text-[#94A3B8]">—</span>
                    )}
                  </div>

                  {/* Column 3: Unit */}
                  <div className="col-span-1 text-center text-xs font-bold text-[#334155]">
                    {details.unit}
                  </div>

                  {/* Column 4: Price & Cost Code */}
                  <div className="col-span-2 flex flex-col items-end justify-center text-right">
                    {details.costCode && (
                      <span className="text-xs font-bold text-[#D97706] mb-0.5">
                        {details.costCode}
                      </span>
                    )}
                  </div>

                  {/* Column 5: Stock */}
                  <div className="col-span-2 text-right text-xs font-bold text-[#0F172A]">
                    {details.stockText}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ProductDropdown;
